/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * ENVOI D'UN TICKET AU PONT ANDROID — avec découpage des gros contenus (repris console)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Un ticket IMAGE (modules `raster` / `raster-hybrid`) pèse 130 à 200 Ko d'hexadécimal.
 * Envoyé en UN SEUL `window.android.print(...)`, ce gros `String` arrive tronqué sur
 * certaines tablettes (passage JS→Java `@JavascriptInterface`) : la balise `<img>` est
 * cassée et l'imprimante sort des mètres d'hexadécimal EN CLAIR. Un ticket texte (~2 Ko)
 * n'est pas concerné.
 *
 * Correctif (identique à `printQueue.sendToAndroid()` de la console) : si le pont expose
 * `printChunk(part)` + `printFlush()`, on envoie par tranches de 16 000 caractères puis on
 * demande l'impression ; côté Java, `printFlush()` réassemble et appelle `print()` inchangé.
 * Sinon (ancienne version de l'app Android) → repli automatique sur `print(...)` : aucune
 * régression. Voir `HANDOFF-pont-usb-image-CHUNK.md` du projet console pour le côté Java.
 */

const CHUNK_SIZE = 16000; // marge large sous les limites observées du passage JS→Java

interface ChunkCapableBridge {
  print?: (content: string) => unknown;
  printChunk?: (part: string) => unknown;
  printFlush?: () => unknown;
}

const bridge = (): ChunkCapableBridge | undefined =>
  (window as unknown as { android?: ChunkCapableBridge }).android;

/** Le pont Android est-il présent (méthode `print`) ? */
export const hasAndroidPrint = (): boolean => typeof bridge()?.print === 'function';

/**
 * Envoie `content` à l'imprimante via le pont Android.
 * Renvoie `false` si le pont est absent (l'appelant choisit alors son repli navigateur).
 * Les exceptions du pont sont propagées, comme avec un appel direct à `print()`.
 */
export const sendToAndroidPrint = (content: string): boolean => {
  const android = bridge();
  if (!android || typeof android.print !== 'function') return false;

  const canChunk =
    typeof android.printChunk === 'function' && typeof android.printFlush === 'function';

  if (content.length > CHUNK_SIZE && canChunk) {
    for (let i = 0; i < content.length; i += CHUNK_SIZE) {
      android.printChunk!(content.slice(i, i + CHUNK_SIZE));
    }
    android.printFlush!();
    console.log(
      `[androidPrint] Ticket envoyé en ${Math.ceil(content.length / CHUNK_SIZE)} tranches (${content.length} car.)`
    );
    return true;
  }

  if (content.length > CHUNK_SIZE) {
    console.warn(
      `[androidPrint] Gros ticket (${content.length} car.) envoyé en un seul bloc : l'app Android ` +
        "n'expose pas printChunk/printFlush. Si l'image sort en hexadécimal, mettre à jour l'APK."
    );
  }
  android.print(content);
  return true;
};
